import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CreditCard, Plus, Trash2, Star, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const brandIcons = { visa: "Visa", mastercard: "Mastercard", amex: "Amex", discover: "Discover" };

export default function PaymentMethods() {
  const { customer, loading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMethods = useCallback(async () => {
    try {
      const token = localStorage.getItem("mahra_session_token");
      const res = await fetch(`${API}/api/customer/payment-methods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMethods(data.payment_methods || []);
      }
    } catch (err) {
      console.warn("[PaymentMethods] fetch failed:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!authLoading && !customer) navigate("/login", { state: { from: "/account/payment-methods" }, replace: true });
    if (customer) fetchMethods();
  }, [customer, authLoading, navigate, fetchMethods]);

  const handleRemove = async (pmId) => {
    if (!window.confirm("Remove this card?")) return;
    try {
      const token = localStorage.getItem("mahra_session_token");
      await fetch(`${API}/api/customer/payment-methods/${pmId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Card removed");
      fetchMethods();
    } catch (err) {
      console.warn("[PaymentMethods] remove failed:", err);
      toast.error("Failed to remove card");
    }
  };

  const handleSetDefault = async (pmId) => {
    try {
      const token = localStorage.getItem("mahra_session_token");
      await fetch(`${API}/api/customer/payment-methods/${pmId}/default`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Default card updated");
      fetchMethods();
    } catch (err) {
      console.warn("[PaymentMethods] set-default failed:", err);
      toast.error("Failed to update default");
    }
  };

  if (authLoading || !customer) return null;

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="max-w-[700px] mx-auto px-4 lg:px-8 py-10 lg:py-16">
        <Link to="/account" className="flex items-center gap-1.5 text-[11px] text-[#676664]/50 tracking-[0.1em] uppercase mb-6 hover:text-[#676664]">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Account
        </Link>
        <h1 className="text-2xl text-[#282828] font-light mb-8" data-testid="payment-methods-title">Payment Methods</h1>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
        ) : (
          <div className="space-y-3">
            {methods.map(pm => (
              <div key={pm.id} className="bg-white border border-[#282828]/8 p-4 lg:p-5 flex items-center gap-4" data-testid={`card-${pm.last4}`}>
                <div className="w-10 h-7 bg-[#FAF8F0] border border-[#282828]/5 flex items-center justify-center text-[9px] text-[#676664] font-medium uppercase">
                  {brandIcons[pm.brand] || pm.brand}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-[#282828]">
                    •••• {pm.last4}
                    {pm.is_default && <span className="ml-2 text-[9px] text-[#9A7B4F] tracking-[0.1em] uppercase">Default</span>}
                  </p>
                  <p className="text-[10px] text-[#676664]/40">Expires {pm.exp_month}/{pm.exp_year}</p>
                </div>
                <div className="flex items-center gap-1">
                  {!pm.is_default && (
                    <button
                      onClick={() => handleSetDefault(pm.id)}
                      className="p-2 text-[#676664]/30 hover:text-[#9A7B4F] transition-colors"
                      title="Set as default"
                      data-testid={`set-default-${pm.last4}`}
                    >
                      <Star className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                  <button
                    onClick={() => handleRemove(pm.id)}
                    className="p-2 text-[#676664]/30 hover:text-red-500 transition-colors"
                    title="Remove card"
                    data-testid={`remove-card-${pm.last4}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}

            {methods.length === 0 && (
              <div className="text-center py-16 bg-white border border-[#282828]/8">
                <CreditCard className="w-10 h-10 text-[#676664]/20 mx-auto mb-4" strokeWidth={1} />
                <p className="text-[#676664]/50 text-sm font-light">No saved payment methods</p>
                <p className="text-[10px] text-[#676664]/30 mt-1">Cards are saved automatically when you make a booking.</p>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
